import React, { useMemo } from "react";
import { useQuizContext } from "@/pages/quiz/context";
import { QuizQuestionProps } from "@/pages/quiz/context/types";
import { ReactComponent as Affirm } from "@/assets/icons/affirm.svg";
import {
  getPreselectedProduct,
  TREATMENT_OPTIONS,
} from "@/pages/quiz/base/questions/selectTreatmentOptionNew/common";
import { Button } from "@/pages/quiz/15000/components/Button";
import { Treatment } from "@/pages/quiz/15000/questions/SelectTreatmentOption/components/Treatment";

type IProps = QuizQuestionProps<{ selectedOptionId: string }>;
type Option = (typeof TREATMENT_OPTIONS)[number];

const SelectTreatmentOptionNew = ({
  setQuestionAnswer,
  setQuestionValue,
  questionValue,
}: IProps) => {
  const { answers } = useQuizContext();

  const addressQuestion = Object.values(answers).find(
    (answer) => answer.blockName === "select-state"
  );

  const selectedState: string = (addressQuestion?.value as string) ?? "";
  const preselectedProductId = getPreselectedProduct();

  const orderedOptions: Option[] = useMemo(() => {
    const sorted = [...TREATMENT_OPTIONS].sort((option) =>
      option.id === preselectedProductId ? -1 : 1
    );
    return sorted.filter(
      (option) => !option.excludedStates.includes(selectedState)
    );
  }, [selectedState, preselectedProductId]);

  const groups: Array<[string, Option[]]> = useMemo(() => {
    const map = new Map<string, Option[]>();
    for (const opt of orderedOptions) {
      const key = opt.variant ?? "Other";
      if (!map.has(key)) map.set(key, []);
      map.get(key)!.push(opt);
    }
    return Array.from(map.entries());
  }, [orderedOptions]);

  const selectOption = (id: string) => () =>
    setQuestionValue({ selectedOptionId: id });

  return (
    <>
      <div className="flex flex-col gap-6">
        {groups.map(([variant, items]) => (
          <div key={variant} className="flex flex-col gap-4">
            <div className="flex gap-4 items-center justify-between">
              <h3 className="sm:text-base text-sm font-bold leading-[140%] text-black px-1 capitalize">
                {variant} Version{`${items.length > 1 ? "s" : ""}`}
              </h3>
              <div className="flex gap-3 items-center">
                <p className="sm:text-base text-sm leading-[140%] font-semibold tracking-[-0.02%]">
                  Buy Now, Pay Later
                </p>
                <Affirm className="-mt-2" />
              </div>
            </div>

            {items.map((option) => {
              const isSelectedOption =
                option.id === questionValue?.selectedOptionId;
              return (
                <Treatment
                  key={option.id}
                  isSelected={isSelectedOption}
                  onSelect={selectOption(option.id)}
                  title={option.title}
                  isNew={option.isNew}
                  img={option.image}
                  price={option.getPrice(selectedState)}
                  labels={option.labels}
                  titleDetails={option.titleDetails}
                  details={option.details}
                />
              );
            })}
          </div>
        ))}
      </div>
      <Button
        disabled={!questionValue?.selectedOptionId}
        onClick={() => setQuestionAnswer(questionValue)}
      />
    </>
  );
};

const SelectTreatmentOptionBundleNew = (props: IProps) => (
  <SelectTreatmentOptionNew {...props} />
);

export { SelectTreatmentOptionNew, SelectTreatmentOptionBundleNew };
